import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { MapPin, Building2, Users, TrendingUp, AlertTriangle, CheckCircle, Clock, LogOut, User, RefreshCw, FileText, QrCode, ShieldCheck, Copy, DollarSign, Bell, Sparkles, Package, Megaphone, KeyRound } from 'lucide-react';
// Iter-163 · Phase 5 — Standalone PM bell removed; unified billing
// notifications now live inside the Billing & A/R tab itself.
import { toast } from 'sonner';
import NECSLogo from '../components/NECSLogo';
import DashboardShell from '../components/DashboardShell';
import { DashboardMenuTabs } from '../components/DashboardMenuTabs';
import { propertyDashboardMenu } from '../lib/dashboardMenus';
import { usePropertyPackages } from '../hooks/usePropertyPackages';
import ComplianceTrophy from '../components/ComplianceTrophy';
import DemoSandboxBadge from '../components/common/DemoSandboxBadge';
import DemoSandboxTour from '../components/common/DemoSandboxTour';
import { getInitials } from '../utils/initials';
import { getPackageDetails, getFlattenedServices, getCanonicalPackageName } from '../lib/packageDetails';
import ViewAsTenantCard from '../components/ViewAsTenantCard';
import QRCodesTab from '../components/QRCodesTab';
import AccessEntryCodesEditor from '../components/AccessEntryCodesEditor';
import ResidentApprovalsQueue from '../components/ResidentApprovalsQueue';
import DailyPickupReportCard from '../components/DailyPickupReportCard';
import TrainingLauncher from '../components/TrainingLauncher';
import UpdatesNoticesPanel, { useUpdatesUnread } from '../components/updates/UpdatesNoticesPanel';
import UpdatesBlockingGate from '../components/updates/UpdatesBlockingGate';
import InvoicesPanel from '../components/InvoicesPanel';
// Iter-158 · Phase 3 — PM dashboard mounts the same four boards used
// by every department command center (same tab-and-board pattern).
import {
  BillingARBoard,
  PropertiesPricingBoard,
  PropertiesContractsBoard,
  PropertiesServicesAgreementBoard,
} from '../components/admin/PropertiesBoards';
import UberTrashDashboard from '../components/UberTrashDashboard';
import CommunityLogBoard from '../components/CommunityLogBoard';
import InlinePhotoAttachments from '../components/InlinePhotoAttachments';
import OwnerEngagementChip from '../components/OwnerEngagementChip';
import UserMenu from '../components/UserMenu';
import NotificationsBell from '../components/NotificationsBell';
import LiveTrackingMap from '../components/LiveTrackingMap';
import PropertyServicesRequest from '../components/PropertyServicesRequest';
import PropertyStaffUsersTab from '../components/PropertyStaffUsersTab';
import WelcomeStrip from '../components/WelcomeStrip';
import ContractedServicesCard from '../components/ContractedServicesCard';
// Iter-163 · Phase 3 — Recommended NECS Services upsell panel.
import RecommendedServicesCard from '../components/RecommendedServicesCard';
import PackageLineupSection from '../components/PackageLineupSection';
// Iter-166 · Phase 1 — standalone Payments tab removed; PaymentMethodsPanel
// is now surfaced solely via the Billing and A/R board.
// Iter-165 · Phase 5 — commercial quote wizard opened inline via
// Explore buttons on the Recommended Services card.
import CommercialQuoteEngine from '../components/CommercialQuoteEngine';
// Iter-166 · Phase 2 — Multifamily quote wizard deep-link from the
// Recommended Services card (Signature Collection Explore CTA).
import MultifamilyQuoteForm from '../components/MultifamilyQuoteForm';
import InviteTeamCard from '../components/InviteTeamCard';
import { ServiceSignalsLegend } from '../components/ServiceSignalsLegend';
import { useProfilePhotoSync } from '../hooks/useProfilePhotoSync';
import ResidentManagementPanel from '../components/ResidentManagementPanel';
import BulkTenantRosterCard from '../components/BulkTenantRosterCard';
import InviteResidentTenantCard from '../components/InviteResidentTenantCard';
import PropertyAccessEntryMirror from '../components/PropertyAccessEntryMirror';
import OwnerKpiStrip from '../components/OwnerKpiStrip';
import { TrashcanRentCharges } from '../components/TrashcanRentCharges';
import { roleTermPluralFor } from '../lib/roleTerm';
import { getPropertyDashboardImage } from '../lib/propertyDashboardImages';

// Iter-148 · Section 2 — Commercial type icons used in the dashboard
// header. Mirror of the LINE_ICON map inside CommercialCleanersBoard so
// the commercial client sees exactly the same emblem the internal ops
// team uses when reviewing their account.
const COMMERCIAL_ICON = {
  office: '🏬', retail: '🛍️', gas_station: '⛽',
  school: '🏫', medical: '🏥', warehouse: '🏭',
  restaurant: '🍽️', mall: '🏦', church: '⛪',
  // Iter-149 · Section 0/9 — 3 new Strategic Enterprise verticals.
  bank: '🏦', stadium: '🏟️', airport: '✈️',
};

function PropertyTrustShareCard({ property }) {
  const url = `${window.location.origin}/property/${property.property_id}/trust`;
  const [snapshots, setSnapshots] = useState([]);

  useEffect(() => {
    if (!property?.property_id) return;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/property/${property.property_id}/trust/snapshots`, { credentials: 'include' });
        if (res.ok) setSnapshots((await res.json()).snapshots || []);
      } catch (_e) { /* noop */ }
    })();
  }, [property?.property_id]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Trust page link copied');
    } catch (_e) { toast.error('Could not copy'); }
  };

  const downloadSnapshot = async (monthKey) => {
    try {
      const qs = monthKey ? `?month=${monthKey}` : '';
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/property/${property.property_id}/trust/snapshot${qs}`, { credentials: 'include' });
      if (!res.ok) { toast.error('Could not download snapshot'); return; }
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u;
      a.download = `NECS_Trust_${(property.property_name || 'Property').replace(/\s+/g, '_')}_${monthKey || 'latest'}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(u);
    } catch (_e) { toast.error('Download failed'); }
  };

  return (
    <Card
      className="p-5 mb-4"
      style={{
        background: 'radial-gradient(circle at 0% 50%, rgba(201,169,98,0.18), transparent 60%), #1a1a1a',
        border: '1px solid #C9A962',
      }}
      data-testid="pm-trust-share-card"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <ShieldCheck className="h-6 w-6 text-[#C9A962] shrink-0" />
          <div className="min-w-0">
            <p className="text-white font-bold leading-tight">Public Trust Page</p>
            <p className="text-xs text-zinc-400 truncate">{url}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="border-[#C9A962] text-[#C9A962]"
            onClick={() => window.open(url, '_blank')}
            data-testid="pm-trust-view"
          >
            View
          </Button>
          <Button
            className="font-bold"
            style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}
            onClick={copy}
            data-testid="pm-trust-copy"
          >
            <Copy className="h-4 w-4 mr-1" /> Copy link
          </Button>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-white/10">
        <div className="flex items-center justify-between mb-2">
          <p className="text-white text-sm font-semibold">Monthly Trust Snapshots</p>
          <Button
            size="sm"
            variant="outline"
            className="border-[#C9A962] text-[#C9A962]"
            onClick={() => downloadSnapshot()}
            data-testid="pm-trust-snapshot-latest"
          >
            <FileText className="h-3.5 w-3.5 mr-1" /> Last month PDF
          </Button>
        </div>
        {snapshots.length === 0 ? (
          <p className="text-[11px] text-zinc-500">No archived snapshots yet — auto-generated on the 1st of each month.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="pm-trust-snapshot-list">
            {snapshots.slice(0, 8).map((s) => (
              <Button
                key={s.month_key}
                size="sm"
                variant="outline"
                className="border-zinc-600 text-zinc-300 justify-between"
                onClick={() => downloadSnapshot(s.month_key)}
                data-testid={`pm-trust-snapshot-${s.month_key}`}
              >
                {s.month_label} <FileText className="h-3.5 w-3.5 ml-1" />
              </Button>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PropertyManagerDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser, loading, logout } = useAuth();
  useProfilePhotoSync(setUser);
  const [property, setProperty] = useState(null);
  const packageCatalog = usePropertyPackages(property);
  const [activeTab, setActiveTab] = useState('tracking');
  // Iter-165 · Phase 5 — inline commercial quote wizard state.
  // `exploreVertical` holds the business_type key (office, retail,
  // bank, stadium, airport, etc.) selected from the Recommended
  // Services card. When non-null the dashboard renders the full-page
  // CommercialQuoteEngine overlay with a Back CTA that clears it.
  const [exploreVertical, setExploreVertical] = useState(null);
  // Iter-167 · Phase 2 — carry the recommended tier key
  // (basic|standard|premium) through into the wizard so the tier row
  // is pre-selected.
  const [exploreTier, setExploreTier] = useState(null);
  // Iter-168 · Phase 3 — inspection-driven prefill (operating_hours +
  // occupants) sourced from the most recent inspection/service event
  // /quote submission for this property. `null` when nothing was
  // detected so the wizard leaves those fields blank.
  const [inspectionPrefill, setInspectionPrefill] = useState(null);
  // Iter-166 · Phase 2 — parallel state for the multifamily wizard
  // deep-link (Signature Collection Explore CTA). Holds the prefill
  // payload or null when the wizard is not showing.
  const [exploreMultifamily, setExploreMultifamily] = useState(null);
  const updatesUnread = useUpdatesUnread();
  const [tracking, setTracking] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showPricingRequest, setShowPricingRequest] = useState(false);
  const [showPackageLineup, setShowPackageLineup] = useState(false);
  const [dailyReports, setDailyReports] = useState([]);
  const [photoGallery, setPhotoGallery] = useState([]);
  const [contracts, setContracts] = useState([]);
  // Red-Team Addendum · PM sub-role gating shared across sidebar + tabs.
  // Iter-190b · Only real Owners / Managers / Assistant Managers see
  // Site Staff Users AND Billing and A/R. Every other sub-role
  // (Leasing Staff, Concierge, Maintenance, etc.) is hidden from
  // those two surfaces on the sidebar AND top tab list.
  const pmSubRoleLC = (user?.pm_sub_role || 'Manager').toString().trim().toLowerCase();
  const occupantsLabel = roleTermPluralFor(property?.property_type || user?.property_type);
  // Legacy multifamily records can lack property_type; only use that fallback
  // after the property is loaded and neither commercial classification is set.
  const snapshotPropertyType = property?.property_type || user?.property_type
    || (property && !property.is_commercial && !property.is_cleaners ? 'residential' : '');
  const snapshotImage = getPropertyDashboardImage(snapshotPropertyType);
  const PM_FULL_ACCESS_SUBROLES = new Set([
    'owner',
    'manager',
    'property_manager', 'property manager',
    'regional_property_manager', 'regional property manager',
    'co-manager', 'co_manager', 'comanager',
    'assistant manager', 'assistant_manager',
    'assistant property manager', 'assistant_property_manager',
    'general_manager', 'general manager',
    'assistant_general_manager', 'assistant general manager',
    'building_manager', 'building manager',
    'assistant_building_manager', 'assistant building manager',
    'branch_manager', 'branch manager',
    'assistant_branch_manager', 'assistant branch manager',
    'store_manager', 'store manager',
    'warehouse_manager', 'warehouse manager',
    'airport_director', 'assistant_director',
    'administrator', 'assistant_administrator',
    'principal', 'assistant_principal',
    'senior_pastor', 'executive_pastor',
  ]);
  const pmFullAccess = PM_FULL_ACCESS_SUBROLES.has(pmSubRoleLC);
  const [pricingForm, setPricingForm] = useState({
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    message: ''
  });

  useEffect(() => {
    if (!loading && !user && !location.state?.user) {
      navigate('/login');
      return;
    }

    if (!user && location.state?.user) {
      setUser(location.state.user);
    }

    if (user?.role !== 'property_manager' && user?.role) {
      if (user.role === 'driver') {
        navigate('/driver-dashboard');
      } else {
        navigate('/tenant-dashboard');
      }
    }
  }, [user, loading, navigate, location.state, setUser]);

  useEffect(() => {
    if (user) {
      setPricingForm({
        contact_name: user.name || '',
        contact_email: user.email || '',
        contact_phone: user.phone || '',
        message: ''
      });
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchPropertyData();
      fetchDailyReports();
      fetchPhotoGallery();
      fetchContracts();
    }
  }, [user]);

  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchPropertyData(true);
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, user]);

  const fetchPropertyData = async (silent = false) => {
    if (!silent) setRefreshing(true);
    
    try {
      // Get property details
      const propResponse = await fetch(`${BACKEND_URL}/api/properties`, {
        credentials: 'include'
      });
      
      if (propResponse.ok) {
        const properties = await propResponse.json();
        // Iter-181 fix — Leasing Staff / Concierge / Maintenance are not
        // the property's manager_id; they're linked via property_id. Fall
        // back to that (then to the first result) so sub-role PMs still
        // see their assigned building.
        const myProperty =
          properties.find(p => p.manager_id === user.user_id) ||
          properties.find(p => p.property_id === user.property_id) ||
          properties[0];
        setProperty(myProperty);

        if (myProperty) {
          // Get tracking data
          const trackingResponse = await fetch(
            `${BACKEND_URL}/api/properties/${myProperty.property_id}/tracking`,
            { credentials: 'include' }
          );
          
          if (trackingResponse.ok) {
            const trackingData = await trackingResponse.json();
            setTracking(trackingData);
            setRoutes(trackingData.active_routes || []);
          }
        }
      }
    } catch (error) {
      if (!silent) {
        console.error('Failed to fetch data:', error);
      }
    } finally {
      if (!silent) setRefreshing(false);
    }
  };

  const fetchDailyReports = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/daily-reports`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setDailyReports(data);
      }
    } catch (error) {
      console.error('Failed to fetch daily reports:', error);
    }
  };

  const fetchPhotoGallery = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/violations`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Extract all photos from violations
        const photos = [];
        data.forEach(v => {
          if (v.photos && v.photos.length > 0) {
            v.photos.forEach(p => {
              photos.push({
                ...p,
                violation_type: v.violation_type,
                unit_number: v.unit_number,
                created_at: v.created_at,
                description: v.description
              });
            });
          }
        });
        setPhotoGallery(photos);
      }
    } catch (error) {
      console.error('Failed to fetch photo gallery:', error);
    }
  };

  const fetchContracts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/contracts`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setContracts(data);
      }
    } catch (error) {
      console.error('Failed to fetch contracts:', error);
    }
  };

  const handlePricingSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${BACKEND_URL}/api/pricing-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...pricingForm,
          property_id: property?.property_id,
          property_name: property?.property_name,
          package_type: property?.package_type
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit pricing request');
      }

      toast.success('Pricing request submitted to Noble Empire');
      setPricingForm((prev) => ({ ...prev, message: '' }));
      setShowPricingRequest(false);
    } catch (error) {
      toast.error(error.message || 'Failed to submit pricing request');
    }
  };

  if (loading) {
    return (
      <>
        <DemoSandboxBadge />
        <DemoSandboxTour />
        <div className="min-h-screen flex items-center justify-center bg-zinc-50">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFD60A]"></div>
            <p className="mt-4 text-zinc-600">Loading...</p>
          </div>
        </div>
      </>
    );
  }

  if (!user || !property) {
    return (
      <>
        <DemoSandboxBadge />
        <DemoSandboxTour />
        {null}
      </>
    );
  }

  const packageInfo = packageCatalog.current;
  const accountMenuItems = propertyDashboardMenu(pmFullAccess, occupantsLabel, updatesUnread);
  const totalUnits = property.total_units || 0;
  const completedToday = tracking?.completed_today || 0;
  const progressPercent = totalUnits > 0 ? Math.round((completedToday / totalUnits) * 100) : 0;

  const allViolations = routes.flatMap(r => r.violations || []);

  return (
    <>
    <DemoSandboxBadge />
    <UpdatesBlockingGate />
    <DemoSandboxTour />
    <DashboardShell
      brandLabel="Property Manager"
      identityProperty={property}
      menuItems={accountMenuItems}
      activeKey={typeof activeTab !== 'undefined' ? activeTab : null}
      onMenuClick={(item) => { if (typeof setActiveTab === 'function') setActiveTab(item.key); }}
      topbarExtras={(
        <button
          type="button"
          onClick={logout}
          className="p-2 rounded-full hover:bg-black/10 transition"
          title="Logout"
          data-testid="pm-topbar-logout"
        >
          <LogOut className="h-5 w-5 text-black" />
        </button>
      )}
      hideUserMenu={true}
    >
    <div className="min-h-full bg-zinc-50 text-zinc-900" data-testid="property-manager-dashboard">
      {/* Inner charcoal navbar — restored to match the CEO-approved layout:
          LEFT  = PROPERTY MANAGER badge + property name
          RIGHT = Owners Engaged chip · Profile avatar · Notifications bell.
          Logout stays on the yellow DashboardShell topbar. */}
      <nav className="sticky top-[49px] sm:top-[65px] z-20" style={{ backgroundColor: '#2D2D2D' }} data-testid="pm-inner-navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20 gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Badge className="bg-[#C9A962] text-black font-bold tracking-widest text-[10px]">PROPERTY MANAGER</Badge>
              <span className="text-white text-sm font-semibold truncate"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.04em' }}>
                {(property?.property_name || 'YOUR COMMUNITY').toUpperCase()}
              </span>
            </div>
            <div className="flex items-center gap-2" data-testid="pm-inner-navbar-cluster">
              <UserMenu />
              <NotificationsBell variant="dark" />
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <WelcomeStrip
          name={user?.name || 'Property Manager'}
          subtitle={`${property?.property_name || 'Property Manager Portal'} · ${property?.total_units || 0} units`}
          testid="pm-welcome-strip"
        />

        {/* Iter-191 · Owner-Only KPI Snapshot. Rendered only for
            pm_sub_role === 'owner'. Occupancy · MTD Revenue · Unit
            Count · Property/Facility ID. */}
        <OwnerKpiStrip user={user} property={property} />

        {/* Iter-191b · Access & Entry card is intentionally NOT rendered
            at the top of the dashboard. It now lives exclusively inside
            the "Access & Entry" menu section (see TabsContent
            value="access-codes" below) so it only populates when the PM
            selects that menu — mirroring how the block appears on the
            registration form. */}
        <div className="mb-6 sm:mb-8">
          {/* Iter-148 · Section 2 — Dynamic header. When the client is one
              of the 9 commercial business types, we surface their NECS
              commercial service line + matching type icon inline above
              the property name. Multifamily clients get the original
              header untouched. */}
          {(property.is_cleaners_demo === true || property.is_commercial === true) && (
            <div
              className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-[0.28em] font-black"
              style={{ color: '#22d3ee' }}
              data-testid="pm-commercial-header-line"
            >
              <span aria-hidden="true" data-testid="pm-commercial-header-icon">
                {COMMERCIAL_ICON[property.property_type] || '🧼'}
              </span>
              <span data-testid="pm-commercial-header-line-name">
                {property.service_line_name || 'NECS Commercial'}
              </span>
              <span className="text-zinc-500">·</span>
              <span className="text-zinc-300 tracking-normal normal-case">
                {(property.property_type_label || property.property_type || '').replace(/_/g, ' ')}
              </span>
            </div>
          )}
          <h1 className="text-2xl sm:text-3xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="pm-property-title">
            {property.property_name}
          </h1>
          <p className="text-sm sm:text-base text-zinc-600">
            {(property.is_cleaners_demo === true || property.is_commercial === true)
              ? `${property.address || ''} · ${property.city || ''}, ${property.state || 'TX'}`
              : `${property.address} • ${property.total_units} Units • ${property.total_buildings} Buildings`}
          </p>
        </div>

        {/* Iter-104 · Item 6 — Cleaners commercial property overview card.
            When the property is one of the 9 non-residential Cleaners
            business types, we render a fully populated overview card so
            the sandbox reads as a real commercial account (never a blank
            page or a Multifamily-only view). */}
        {(property.is_cleaners_demo === true || property.is_commercial === true) && (
          <Card
            className="mb-6 sm:mb-8 p-5 border-2"
            style={{ borderColor: '#22d3ee', backgroundColor: 'rgba(34,211,238,0.08)' }}
            data-testid="pm-cleaners-overview"
          >
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-[0.28em] font-bold mb-1" style={{ color: '#22d3ee' }}>
                  Cleaners HQ · {property.service_line_name || 'NECS Commercial'}
                </p>
                <h3 className="text-xl font-black text-white mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {(property.property_type_label || property.property_type || '').toString().replace(/_/g, ' ').toUpperCase()}
                </h3>
                <p className="text-xs text-zinc-300">
                  Service tier: <span className="font-bold text-white">{property.tier || 'Custom'}</span>
                  {property.is_demo && (
                    <span className="ml-2 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid #fbbf24' }}>
                      Demo sandbox
                    </span>
                  )}
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                {[
                  { label: 'Sq Ft',   value: property.square_footage || property?.property_plan_summary?.total_sqft || '—' },
                  { label: 'Floors',  value: property.number_of_floors || property?.property_plan_summary?.floor_count || '—' },
                  { label: 'Rooms',   value: property?.property_plan_summary?.rooms_units || property.total_units || '—' },
                  { label: 'Lot',     value: property?.property_plan_summary?.lot_size || '—' },
                ].map((s) => (
                  <div key={s.label} className="rounded px-2 py-1" style={{ backgroundColor: '#0f0f0f', border: '1px solid #22d3ee' }}>
                    <p className="text-[9px] uppercase tracking-widest" style={{ color: '#22d3ee' }}>{s.label}</p>
                    <p className="text-sm font-black text-white">{s.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* Iter-194 · Red-Team fix — the "Amenities Serviced by NECS"
            chip strip was moved off the main welcome dashboard and now
            lives exclusively inside the Access & Entry menu (through
            the PropertyAccessEntryMirror card), where the same
            amenities are shown alongside their gate/entry codes. */}

        {/* Industry-matched upper-body banners for the12 commercial types,
            plus the supplied Multifamily leasing-office image. Shared by every
            Owner, Property Manager and Office sub-role without role-based copies. */}
        <Card className="overflow-hidden mb-6 sm:mb-8 border-2 border-[#C9A962] bg-zinc-900" data-testid="pm-snapshot-banner">
          <img
            src={snapshotImage?.src || 'https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/xg81tmnq_1565.jpg'}
            alt={snapshotImage?.alt || 'Noble Empire NECS crew preparing service at Summit Business Center'}
            className={snapshotImage ? 'block w-full h-auto object-cover' : 'w-full h-48 sm:h-56 object-cover'}
            style={snapshotImage ? { aspectRatio: '3 / 1', objectPosition: snapshotImage.objectPosition } : undefined}
            width={snapshotImage ? 1536 : undefined}
            height={snapshotImage ? 512 : undefined}
            data-testid="pm-snapshot-image"
          />
          <div className="p-4 sm:p-5 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 55%, #2a2010 100%)' }}>
            <div className="absolute left-0 right-0 top-0 h-[2px]" style={{ background: 'linear-gradient(to right, transparent, #C9A962 30%, #FFD60A 50%, #C9A962 70%, transparent)' }} />
            <p
              className="text-[10px] uppercase tracking-[0.3em] mb-1.5"
              style={{ color: '#C9A962', fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 600 }}
            >
              Noble Empire · Property Management Partner
            </p>
            <h2
              className="necs-dashboard-caption text-base sm:text-lg leading-snug"
              style={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontStyle: 'italic',
                fontWeight: 600,
                color: '#ffffff',
                textShadow: '0 1px 6px rgba(0,0,0,0.5)',
              }}
              data-testid="pm-snapshot-tagline"
            >
              Setting the Standard for Property Care, Community Cleanliness &amp; Operational Excellence.
            </h2>
          </div>
        </Card>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <Card className="p-4 sm:p-6 border-l-4 border-l-[#FFD60A]">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs sm:text-sm font-semibold text-zinc-600">TOTAL UNITS</h3>
              <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-[#FFD60A]" />
            </div>
            <p className="text-2xl sm:text-3xl font-black">{totalUnits}</p>
          </Card>

          <Card className="p-4 sm:p-6 border-l-4 border-l-green-500">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs sm:text-sm font-semibold text-zinc-600">COMPLETED</h3>
              <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-500" />
            </div>
            <p className="text-2xl sm:text-3xl font-black text-green-600">{completedToday}</p>
          </Card>

          <Card className="p-4 sm:p-6 border-l-4 border-l-blue-500">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs sm:text-sm font-semibold text-zinc-600">PROGRESS</h3>
              <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />
            </div>
            <p className="text-2xl sm:text-3xl font-black text-blue-600">{progressPercent}%</p>
          </Card>

          <Card className="p-4 sm:p-6 border-l-4 border-l-red-500">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs sm:text-sm font-semibold text-zinc-600">VIOLATIONS</h3>
              <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-red-500" />
            </div>
            <p className="text-2xl sm:text-3xl font-black text-red-600">{allViolations.length}</p>
          </Card>
        </div>

        {/* View-as-Tenant — impersonation preview for support / troubleshooting. */}
        <ViewAsTenantCard property={property} />

        {/* SERVICE PACKAGE — matches the Resident/Tenant treatment: dark
            charcoal gradient background, gold headline, high-contrast white
            service bullets that stay readable over any screenshot the PM
            takes for their owners. Uses getFlattenedServices() so inherited
            "Everything in X Collection" lines are expanded — no placeholder
            copy in front of the client. */}
        <Card
          className="p-6 mb-8 border-2 relative overflow-hidden"
          style={{
            borderColor: '#C9A962',
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2D2D2D 65%, #3a2f15 100%)',
          }}
          data-testid="pm-package-card"
        >
          <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#C9A962' }}>
                <Package className="h-7 w-7 text-black" />
              </div>
              <div>
                <p
                  className="text-[10px] uppercase tracking-[0.3em] mb-1"
                  style={{ color: '#C9A962', fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  Contracted Service Package
                </p>
                <h2
                  className="text-2xl sm:text-4xl font-extrabold uppercase tracking-wide leading-tight"
                  style={{
                    fontFamily: 'Barlow Condensed, sans-serif',
                    color: '#FFD60A',
                    textShadow: '0 2px 6px rgba(0,0,0,0.5)',
                    letterSpacing: '0.05em',
                  }}
                  data-testid="pm-package-name"
                >
                  {packageCatalog.loading ? 'Loading approved package…' : packageInfo.name}
                </h2>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className={`h-3 w-3 rounded-full ${autoRefresh ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
              <span className="text-sm text-zinc-300">
                {autoRefresh ? 'Live Updates' : 'Paused'}
              </span>
            </div>
          </div>
          <p className="text-xs text-zinc-300 mb-4">
            Schedule · <strong className="text-[#C9A962]">{packageInfo.shift}</strong>
          </p>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1.5" data-testid="pm-package-services">
            {packageInfo.services.map((service, idx) => (
              <li key={idx} className="flex items-start text-sm text-white">
                <CheckCircle className="h-4 w-4 text-[#C9A962] mr-2 flex-shrink-0 mt-0.5" />
                <span>{service}</span>
              </li>
            ))}
          </ul>
          {packageCatalog.error && <p className="text-sm text-red-300" role="alert" data-testid="pm-current-package-error">{packageCatalog.error}</p>}
          <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-[11px] text-zinc-400 italic" data-testid="pricing-request-note">
              * Every service is listed for transparency — no &ldquo;Everything in X&rdquo; placeholders.
              Upgrade requests are handled personally by the Noble Empire management team.
            </p>
            <Button
              className="font-bold shrink-0"
              style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}
              onClick={() => setShowPackageLineup((v) => !v)}
              data-testid="request-upgrade-button"
              aria-expanded={showPackageLineup}
            >
              {showPackageLineup ? 'HIDE PACKAGES' : 'REQUEST UPGRADE'}
            </Button>
          </div>
        </Card>

        {/* Master Addendum · Full NECS Package Line-up.
            Only renders once REQUEST UPGRADE is clicked so the dashboard
            stays uncluttered. Works for Multifamily + the 12 commercial
            business types (cleaners) via PackageLineupSection's business
            type inference. */}
        {showPackageLineup && (
          <div data-testid="pm-package-lineup-wrap">
            <PackageLineupSection property={property} user={user} />
          </div>
        )}

        <Dialog open={showPricingRequest} onOpenChange={setShowPricingRequest}>
          <DialogContent className="sm:max-w-lg" data-testid="pricing-request-dialog">
            <DialogHeader>
              <DialogTitle>Request Pricing</DialogTitle>
            </DialogHeader>
            <form onSubmit={handlePricingSubmit} className="space-y-4">
              <div>
                <Label htmlFor="pricing-name">Contact Name</Label>
                <Input
                  id="pricing-name"
                  value={pricingForm.contact_name}
                  onChange={(e) => setPricingForm({ ...pricingForm, contact_name: e.target.value })}
                  required
                  data-testid="pricing-name-input"
                />
              </div>
              <div>
                <Label htmlFor="pricing-email">Contact Email</Label>
                <Input
                  id="pricing-email"
                  type="email"
                  value={pricingForm.contact_email}
                  onChange={(e) => setPricingForm({ ...pricingForm, contact_email: e.target.value })}
                  required
                  data-testid="pricing-email-input"
                />
              </div>
              <div>
                <Label htmlFor="pricing-phone">Contact Phone</Label>
                <Input
                  id="pricing-phone"
                  value={pricingForm.contact_phone}
                  onChange={(e) => setPricingForm({ ...pricingForm, contact_phone: e.target.value })}
                  data-testid="pricing-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="pricing-message">Request Details</Label>
                <Textarea
                  id="pricing-message"
                  value={pricingForm.message}
                  onChange={(e) => setPricingForm({ ...pricingForm, message: e.target.value })}
                  rows={4}
                  placeholder="Share preferred start date, service frequency, or any questions."
                  data-testid="pricing-message-input"
                />
              </div>
              <div className="rounded-lg bg-zinc-100 p-3 text-xs text-zinc-600" data-testid="pricing-property-summary">
                Property: {property.property_name} • Package: {property.package_type}
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowPricingRequest(false)}
                  data-testid="pricing-cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="font-bold"
                  style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}
                  data-testid="pricing-submit-button"
                >
                  Submit Request
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          {/* Iter-165 · Phase 5 — inline commercial quote wizard
              overlay when a PM clicks Explore on the Recommended
              Services card. Hides the tab content underneath. */}
          {exploreVertical && (
            <div className="rounded-xl border p-2"
                 style={{ backgroundColor: '#0f0f0f', borderColor: '#3f3f46' }}
                 data-testid="explore-quote-overlay">
              <CommercialQuoteEngine
                initialType={exploreVertical}
                prefill={{
                  business_type: exploreVertical,
                  // Iter-167 · Phase 2 — tier row pre-selected from
                  // the tapped Recommended package (basic|standard|premium).
                  tier: exploreTier || 'standard',
                  property_name: property?.name || property?.property_name || '',
                  address: property?.address || property?.street_address || '',
                  square_footage: property?.square_footage ? String(property.square_footage) : '',
                  buildings: property?.buildings ? String(property.buildings) : '1',
                  floors: property?.floors ? String(property.floors) : '1',
                  // Iter-168 · Phase 3 — auto-detected operating hours
                  // + occupant count from the property's most recent
                  // inspection / quote / event. Falls back to the
                  // existing property fields when nothing was found.
                  operating_hours: (inspectionPrefill?.operating_hours)
                    || property?.operating_hours || '',
                  occupants: (inspectionPrefill?.occupants != null
                              ? String(inspectionPrefill.occupants)
                              : (property?.total_units ? String(property.total_units) : '')),
                  inspection_prefill_source: inspectionPrefill?.source || null,
                  inspection_prefill_detected_at: inspectionPrefill?.detected_at || null,
                  full_name: user?.full_name || '',
                  company_name: property?.company_name || '',
                  email: user?.email || '',
                  phone: user?.phone || property?.phone || '',
                }}
                onBack={() => {
                  setExploreVertical(null);
                  setExploreTier(null);
                  setInspectionPrefill(null);
                }}
              />
            </div>
          )}
          {/* Iter-166 · Phase 2 — Multifamily quote wizard overlay. */}
          {exploreMultifamily && (
            <div className="rounded-xl border p-2"
                 style={{ backgroundColor: '#0f0f0f', borderColor: '#3f3f46' }}
                 data-testid="explore-multifamily-overlay">
              <MultifamilyQuoteForm
                preselectPackage={exploreMultifamily.preselectPackage}
                prefill={exploreMultifamily}
                onBack={() => setExploreMultifamily(null)}
              />
            </div>
          )}
          <DashboardMenuTabs items={accountMenuItems} prefix="pm" />

          <TabsContent value="tracking" className="space-y-4">
            <LiveTrackingMap />
            <Card className="p-6">
              <h3 className="text-xl font-bold mb-4" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                REAL-TIME PROPERTY STATUS
              </h3>
              
              {property.property_plan_url && (
                <div className="mb-6 p-4 bg-zinc-100 rounded-lg">
                  <p className="text-sm font-semibold mb-2">Property Layout:</p>
                  <img 
                    src={property.property_plan_url} 
                    alt="Property Plan" 
                    className="w-full max-w-2xl rounded border border-zinc-300"
                  />
                </div>
              )}

              <div className="space-y-4">
                {routes.length === 0 ? (
                  <div className="text-center py-12">
                    <Clock className="h-12 w-12 text-zinc-400 mx-auto mb-4" />
                    <p className="text-zinc-600">No active routes for today</p>
                    <p className="text-sm text-zinc-500">Drivers will appear here when they start their routes</p>
                  </div>
                ) : (
                  routes.map((route) => {
                    const lastLocation = route.last_location;
                    
                    return (
                      <Card key={route.route_id} className="p-4 sm:p-6 bg-gradient-to-r from-white to-zinc-50">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="font-bold text-base sm:text-lg flex items-center">
                            {route.driver_name}
                            {lastLocation && (
                              <span className="ml-2 flex items-center">
                                <MapPin className="h-4 w-4 text-green-500 animate-pulse" />
                                <span className="text-xs text-green-600 ml-1">Live</span>
                              </span>
                            )}
                          </h4>
                          <p className="text-xs sm:text-sm text-zinc-600">{route.shift_time}</p>
                          {lastLocation && (
                            <p className="text-xs text-zinc-500 mt-1">
                              📍 Lat: {lastLocation.latitude?.toFixed(6)}, Lng: {lastLocation.longitude?.toFixed(6)}
                            </p>
                          )}
                        </div>
                        <Badge className={route.status === 'completed' ? 'bg-green-500' : 'bg-blue-500'}>
                          {route.status}
                        </Badge>
                      </div>

                      <div className="mb-4">
                        <div className="flex justify-between text-sm mb-2">
                          <span>Progress: {route.completed_units.length} / {route.units.length} units</span>
                          <span className="font-bold">
                            {Math.round((route.completed_units.length / route.units.length) * 100)}%
                          </span>
                        </div>
                        <div className="w-full bg-zinc-200 rounded-full h-3">
                          <div 
                            className="bg-[#FFD60A] h-3 rounded-full transition-all duration-500"
                            style={{width: `${(route.completed_units.length / route.units.length) * 100}%`}}
                          ></div>
                        </div>
                      </div>

                      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                        {route.units.map((unit) => {
                          const isCompleted = route.completed_units.includes(unit);
                          const hasViolation = route.violations.some(v => v.unit_number === unit);
                          
                          return (
                            <div
                              key={unit}
                              className={`p-2 sm:p-3 rounded text-center font-bold text-xs sm:text-sm transition-all ${
                                hasViolation 
                                  ? 'bg-red-500 text-white' 
                                  : isCompleted 
                                  ? 'bg-green-500 text-white' 
                                  : 'bg-gray-200 text-gray-600'
                              }`}
                              title={hasViolation ? 'Violation' : isCompleted ? 'Completed' : 'Pending'}
                            >
                              {unit}
                            </div>
                          );
                        })}
                      </div>

                      {route.violations.length > 0 && (
                        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
                          <p className="text-sm font-semibold text-red-800">
                            {route.violations.length} Violation(s) Reported
                          </p>
                        </div>
                      )}
                    </Card>
                    );
                  })
                )}
              </div>
            </Card>

            <div className="grid grid-cols-3 gap-4">
              <Card className="p-4">
                <div className="flex items-center space-x-2">
                  <div className="h-4 w-4 bg-green-500 rounded"></div>
                  <span className="text-sm font-semibold">Completed</span>
                </div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center space-x-2">
                  <div className="h-4 w-4 bg-gray-200 rounded"></div>
                  <span className="text-sm font-semibold">Pending</span>
                </div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center space-x-2">
                  <div className="h-4 w-4 bg-red-500 rounded"></div>
                  <span className="text-sm font-semibold">Violation</span>
                </div>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="services" className="space-y-4">
            <PropertyServicesRequest user={{ ...user, property_name: property?.property_name }} />
          </TabsContent>

          {/* Site Staff Users — PM can add their on-site team members
              (assistants, leasing leads, co-managers). Each created user
              gets a profile + login to this property's PM dashboard.
              Iter-190b · Only Owners / Managers / Assistant Managers see
              this surface — matches the sidebar gating above. */}
          {pmFullAccess && (
            <TabsContent value="site-staff" className="space-y-4">
              <PropertyStaffUsersTab propertyId={property?.property_id} propertyName={property?.property_name} />
            </TabsContent>
          )}

          <TabsContent value="drivers">
            <Card className="p-6">
              <h3 className="text-xl font-bold mb-4" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                ACTIVE DRIVERS
              </h3>
              {routes.length === 0 ? (
                <p className="text-center text-zinc-600 py-8">No active drivers</p>
              ) : (
                <div className="space-y-4">
                  {routes.map((route) => (
                    <Card key={route.route_id} className="p-4 flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="h-12 w-12 bg-[#FFD60A] rounded-full flex items-center justify-center">
                          <Users className="h-6 w-6 text-black" />
                        </div>
                        <div>
                          <p className="font-bold">{route.driver_name}</p>
                          <p className="text-sm text-zinc-600">
                            {route.completed_units.length} of {route.units.length} units completed
                          </p>
                        </div>
                      </div>
                      <Badge className={route.status === 'completed' ? 'bg-green-500' : 'bg-blue-500'}>
                        {route.status}
                      </Badge>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="residents" className="space-y-4">
            <ResidentApprovalsQueue propertyType={property?.property_type || user?.property_type} />
            <TrashcanRentCharges propertyId={property?.property_id} canManage={pmFullAccess} />
            <Card className="p-6 space-y-4">
              {/* Iter-191 · Invite New Residents/Tenants — placed above
                  everything else so PMs can push invites without
                  scrolling. Auto-labels for Multifamily (Residents) vs
                  the 12 commercial verticals (Tenants). */}
              <InviteResidentTenantCard
                propertyId={property?.property_id}
                propertyCode={property?.necs_code}
                propertyName={property?.property_name}
                propertyType={property?.property_type}
              />
              <BulkTenantRosterCard
                propertyId={property?.property_id}
                propertyCode={property?.necs_code}
                propertyName={property?.property_name}
                propertyType={property?.property_type}
              />
              {/* S6 Phase 3 — Resident Management for the PM portal. */}
              <ResidentManagementPanel propertyId={property?.property_id} propertyName={property?.property_name} propertyType={property?.property_type} />
            </Card>
          </TabsContent>

          <TabsContent value="violations">
            <Card className="p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                <AlertTriangle className="h-6 w-6 text-red-500 mr-2" />
                VIOLATION REPORTS
              </h3>
              {allViolations.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <p className="text-zinc-600">No violations reported today</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {allViolations.map((violation, idx) => (
                    <Card key={idx} className="p-4 border-l-4 border-l-red-500">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-bold">Unit {violation.unit_number}</p>
                          <p className="text-sm text-zinc-600 mt-1">{violation.violation_type}</p>
                          <p className="text-sm text-zinc-700 mt-1">{violation.description}</p>
                        </div>
                        <p className="text-xs text-zinc-500">
                          {new Date(violation.timestamp || violation.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                      {/* Iter-176 · Phase 5 — Inline photo attachments
                          on the violation record. */}
                      {violation.violation_id && (
                        <div className="mt-3 pt-3 border-t border-zinc-200">
                          <InlinePhotoAttachments
                            parentKind="violation"
                            parentId={violation.violation_id}
                            testIdPrefix={`viol-${violation.violation_id}`}
                          />
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Iter-176 · Phase 4 — Photo Gallery TabsContent removed.
              Photos remain accessible inline via Service Requests and
              Violations detail rows (see InlinePhotoAttachments). */}

          <TabsContent value="daily-reports">
            <a href="/operation-records" className="inline-flex text-[#C9A962] underline mb-4" data-testid="pm-validated-property-reports-link">Supervisor-validated property service reports</a>
            {property && <PropertyTrustShareCard property={property} />}
            <ServiceSignalsLegend
              propertyId={property?.property_id}
              className="mt-3"
            />
            {property && (
              <DailyPickupReportCard
                propertyOptions={[property]}
                defaultPropertyId={property.property_id}
                dataTestId="pm-daily-report-card"
              />
            )}
            <Card className="p-6 mt-4">
              <h3 className="text-xl font-bold mb-4" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                DAILY SERVICE REPORTS
              </h3>
              {dailyReports.length === 0 ? (
                <p className="text-center text-zinc-600 py-8">No daily reports submitted yet</p>
              ) : (
                <div className="space-y-4">
                  {dailyReports.map((report) => {
                    const completedCount = Object.values(report.checklist || {}).filter(Boolean).length;
                    return (
                      <Card key={report.report_id} className="p-4 bg-zinc-50 border border-zinc-200">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-bold text-zinc-900">{report.report_date}</p>
                            <p className="text-sm text-zinc-600">Supervisor: {report.user_name} • {report.hours_worked} hrs</p>
                          </div>
                          <Badge className="bg-[#FFD60A] text-black">
                            {completedCount} items checked
                          </Badge>
                        </div>
                        {report.notes && (
                          <p className="text-sm text-zinc-700 mt-3 p-3 bg-white border border-zinc-200 rounded">
                            {report.notes}
                          </p>
                        )}
                      </Card>
                    );
                  })}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="contracts" className="space-y-6">
            {/* Contracted Services — parity with the Contractor / Staff view.
                Same data source (property.service_policies) + same display
                labels from lib/specialtyServices.js, so the PM can confirm
                they're getting exactly what the field team is scheduled
                to deliver. */}
            {property && (
              <ContractedServicesCard
                property={property}
                viewerRole="property_manager"
              />
            )}
            {property && (
              <RecommendedServicesCard
                property={property}
                accountType={
                  // Iter-165 · Phase 5 fix — treat both `account_type=='commercial'`
                  // AND `is_commercial===true` (or a known commercial
                  // property_type key) as commercial so the Explore
                  // deep-link surfaces the correct vertical.
                  (property?.account_type === 'commercial'
                    || property?.is_commercial === true
                    || ['office','retail','gas_station','school','medical','warehouse',
                        'restaurant','church','mall','bank','stadium','airport'].includes(
                          (property?.property_type || '').toString()))
                    ? 'commercial' : 'multifamily'
                }
                autopayActive={Boolean(property?.autopay_active)}
                onExploreAutopay={() => setActiveTab && setActiveTab('billing-ar')}
                onExplorePackage={(pkg) => {
                  // Prefer the vertical_key emitted by RecommendedServicesCard
                  // for commercial packages; fall back to property_type
                  // when the card is in multifamily mode but the property
                  // is actually commercial.
                  const vertical = pkg?.vertical_key
                    || (property?.property_type && ['office','retail','gas_station','school',
                         'medical','warehouse','restaurant','church','mall','bank','stadium',
                         'airport'].includes(property.property_type) ? property.property_type : null);
                  if (vertical) {
                    // Iter-167 · Phase 2 — pre-select the matching
                    // tier row so the PM lands ready to configure.
                    // Iter-168 · Phase 3 — fetch inspection-driven
                    // prefill FIRST, then open the overlay in a
                    // single render so the wizard's initial state
                    // includes operating_hours + occupants.
                    setExploreTier(pkg?.tier_key || null);
                    setInspectionPrefill(null);
                    const pid = property?.property_id;
                    const openOverlay = () => setExploreVertical(vertical);
                    if (pid) {
                      fetch(`${BACKEND_URL}/api/inspections/prefill/${encodeURIComponent(pid)}`,
                            { credentials: 'include' })
                        .then((r) => r.ok ? r.json() : null)
                        .then((d) => { if (d) setInspectionPrefill(d); })
                        .catch(() => {})
                        .finally(openOverlay);
                    } else {
                      openOverlay();
                    }
                    return;
                  }
                  // Iter-166 · Phase 2 — multifamily deep-link into the
                  // multifamily quote wizard with property metrics
                  // prefilled. Unavailable fields stay blank.
                  setExploreMultifamily({
                    preselectPackage: pkg?.name,
                    unit_count: property?.total_units || property?.num_units || property?.unit_count || '',
                    property_name: property?.property_name || property?.name || '',
                    property_address: property?.address || property?.street_address || '',
                    full_name: user?.full_name || user?.name || '',
                    email: user?.email || '',
                    phone: user?.phone || property?.phone || '',
                  });
                }}
              />
            )}

            {/* Invite team as Demo Residents — critical for the sandbox
                walkthrough. Demo PMs share the copy-able link so multiple
                colleagues can log in simultaneously and see live tracking
                + milestone alerts update in real-time. Production PMs use
                this to onboard actual new residents to the property. */}
            {property && (
              <InviteTeamCard
                property={property}
                isDemo={Boolean(property.is_lead_demo || property.demo_org_id)}
              />
            )}

            <Card className="p-6">
              <h3 className="text-xl font-bold mb-4" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                PROPERTY CONTRACTS
              </h3>
              <p className="text-sm text-zinc-600 mb-6">View and download your active service agreements.</p>
              
              {contracts.length === 0 ? (
                <div className="text-center py-8 bg-zinc-50 rounded-lg border border-dashed border-zinc-300">
                  <FileText className="h-10 w-10 text-zinc-400 mx-auto mb-3" />
                  <p className="text-zinc-600">No contracts available</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {contracts.map((contract) => (
                    <Card key={contract.contract_id} className="p-4 flex items-center justify-between border-l-4 border-l-[#FFD60A]">
                      <div>
                        <p className="font-bold text-zinc-900">{contract.original_filename}</p>
                        <p className="text-xs text-zinc-500">Uploaded: {new Date(contract.created_at).toLocaleDateString()}</p>
                      </div>
                      <Button
                        variant="outline"
                        className="border-[#C9A962] text-[#C9A962] hover:bg-[#C9A962] hover:text-white"
                        onClick={() => window.open(`${BACKEND_URL}/api/contracts/${contract.contract_id}/download`, '_blank')}
                      >
                        Download
                      </Button>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="qr-codes">
            <QRCodesTab isPropertyManager dataTestId="pm-qr-codes" />
          </TabsContent>

          <TabsContent value="access-codes" className="space-y-4">
            {/* Iter-191c · Access & Entry now mirrors the EXACT
                disposition the applicant filled in at registration —
                Property Gate Code + Amenities & Entry Codes checkbox
                grid keyed to this property's vertical. Editable for
                Owner / Manager / Assistant Manager (pmFullAccess);
                read-only for every other sub-role. */}
            <PropertyAccessEntryMirror
              propertyId={property?.property_id}
              readOnly={!pmFullAccess}
            />
          </TabsContent>

          {/* Iter-172 · Phase 2 — Invoices TabsContent removed. */}

          <TabsContent value="uber-trash" className="space-y-4">
            <UberTrashDashboard />
          </TabsContent>

          {/* Updates & Notices — company update feed; Training preserved
              as a sub-tab. */}
          <TabsContent value="updates" className="space-y-4">
            <UpdatesNoticesPanel testIdPrefix="pm-updates"
              trainingSlot={<TrainingLauncher dataTestId="pm-training-launcher" />} />
          </TabsContent>

          {/* Iter-158 · Phase 3 — PM parity boards. Reuse the exact
              same four tab-boards the department CCs use so a PM sees
              their own account state in the same layout. */}
          {/* Red-Team Addendum · Billing and A/R content is Manager-only. */}
          {pmFullAccess && (
            <TabsContent value="billing-ar" className="space-y-4" data-testid="pm-content-billing-ar">
              <BillingARBoard pmContext={{
                property_id: user?.property_id,
                customer_id: user?.user_id || user?.email,
                customer_email: user?.email,
              }} />
            </TabsContent>
          )}
          {/* Iter-175 · Phase 6 — Community Log tab (sibling of Billing
              and A/R). Live resident confirmations, PM-scoped by the
              backend, filterable by date + status, auto-refreshes. */}
          <TabsContent value="community-log" className="space-y-4" data-testid="pm-content-community-log">
            <CommunityLogBoard
              propertyId={property?.property_id}
              propertyName={property?.property_name}
            />
          </TabsContent>
          {/* Iter-166 · Phase 1 — dedicated Payments tab removed. All four
              payment methods now live inside the Billing and A/R board
              above (pm-content-billing-ar) via BillingARBoard → PaymentMethodsPanel. */}
          <TabsContent value="properties-pricing" className="space-y-4" data-testid="pm-content-properties-pricing">
            <PropertiesPricingBoard />
          </TabsContent>
          <TabsContent value="properties-contracts" className="space-y-4" data-testid="pm-content-properties-contracts">
            <PropertiesContractsBoard />
          </TabsContent>
          <TabsContent value="properties-services" className="space-y-4" data-testid="pm-content-properties-services">
            <PropertiesServicesAgreementBoard />
          </TabsContent>
        </Tabs>
      </div>
    </div>
    </DashboardShell>
    </>
  );
}
